import { openDB } from "idb";

export const WORKOUT_LOG_DB_NAME = "lz-workout-log";
export const WORKOUT_LOG_DB_VERSION = 1;

export const WORKOUT_LOG_STORES = Object.freeze({
  META: "meta",
  EXECUTIONS: "executions",
  PREFERENCES: "preferences",
  QUARANTINE: "quarantine",
});

const databasePromises = new Map();

export class UnsupportedWorkoutLogDatabaseVersionError extends Error {
  constructor(cause) {
    super("This workout data was created by a newer version of Lyft Zone.");
    this.name = "UnsupportedWorkoutLogDatabaseVersionError";
    this.code = "CLIENT_SCHEMA_UNSUPPORTED";
    this.cause = cause;
  }
}

function ensureIndex(store, name, keyPath, options) {
  if (!store.indexNames.contains(name)) {
    store.createIndex(name, keyPath, options);
  }
}

export function upgradeWorkoutLogSchema(
  database,
  oldVersion,
  transaction,
  targetVersion = WORKOUT_LOG_DB_VERSION
) {
  if (oldVersion > targetVersion) {
    transaction.abort();
    throw new UnsupportedWorkoutLogDatabaseVersionError();
  }

  if (!database.objectStoreNames.contains(WORKOUT_LOG_STORES.META)) {
    database.createObjectStore(WORKOUT_LOG_STORES.META);
  }

  let executions;
  if (!database.objectStoreNames.contains(WORKOUT_LOG_STORES.EXECUTIONS)) {
    executions = database.createObjectStore(WORKOUT_LOG_STORES.EXECUTIONS, {
      keyPath: "executionId",
    });
  } else {
    executions = transaction.objectStore(WORKOUT_LOG_STORES.EXECUTIONS);
  }
  ensureIndex(executions, "byUser", "userId");
  ensureIndex(executions, "byUserStatus", ["userId", "status"]);
  // IndexedDB keys cannot contain booleans. Keep the canonical `pending`
  // boolean on the record and use its numeric mirror only for this index.
  ensureIndex(executions, "byUserPending", ["userId", "pendingKey"]);

  if (!database.objectStoreNames.contains(WORKOUT_LOG_STORES.PREFERENCES)) {
    database.createObjectStore(WORKOUT_LOG_STORES.PREFERENCES, {
      keyPath: "userId",
    });
  }

  let quarantine;
  if (!database.objectStoreNames.contains(WORKOUT_LOG_STORES.QUARANTINE)) {
    quarantine = database.createObjectStore(WORKOUT_LOG_STORES.QUARANTINE, {
      keyPath: "id",
      autoIncrement: true,
    });
  } else {
    quarantine = transaction.objectStore(WORKOUT_LOG_STORES.QUARANTINE);
  }
  ensureIndex(quarantine, "byUser", "userId");

  transaction.objectStore(WORKOUT_LOG_STORES.META)
    .put(targetVersion, "dbSchemaVersion");
}

export async function openWorkoutLogDb({
  name = WORKOUT_LOG_DB_NAME,
  openDb = openDB,
} = {}) {
  if (typeof indexedDB === "undefined") {
    const error = new Error("IndexedDB is not available in this browser.");
    error.code = "STORAGE_UNAVAILABLE";
    throw error;
  }

  if (openDb === openDB && databasePromises.has(name)) {
    return databasePromises.get(name);
  }

  let upgradeError = null;
  const opening = openDb(name, WORKOUT_LOG_DB_VERSION, {
      upgrade(database, oldVersion, _newVersion, transaction) {
        try {
          upgradeWorkoutLogSchema(database, oldVersion, transaction);
        } catch (error) {
          upgradeError = error;
          throw error;
        }
      },
    }).then((database) => {
      if (openDb === openDB) {
        database.addEventListener("versionchange", () => {
          database.close();
          databasePromises.delete(name);
        });
      }
      return database;
    });
  if (openDb === openDB) databasePromises.set(name, opening);

  try {
    return await opening;
  } catch (error) {
    if (openDb === openDB) databasePromises.delete(name);
    if (upgradeError) throw upgradeError;
    if (error?.name === "VersionError") {
      throw new UnsupportedWorkoutLogDatabaseVersionError(error);
    }
    throw error;
  }
}

export async function closeWorkoutLogDb(name = WORKOUT_LOG_DB_NAME) {
  const opening = databasePromises.get(name);
  databasePromises.delete(name);
  if (!opening) return;
  const database = await opening.catch(() => null);
  database?.close();
}
